import {
  createContext,
  type FC,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { EvaluatedFeatureFlag } from "@b2bportal/commbank-au-feature-flags-api";
import type {
  ExperimentState,
  ExperimentsType,
  IApiConfig,
  TrackingPropertiesType,
} from "@hopper-b2b/types";
import { initFeatureFlagsState } from "./utils/initFeatureFlagsState";

export const HIDE_CC = "ctr-marketplace-hidecreditcard";

export interface FeatureFlagsContext extends ExperimentState {
  featureFlags: Array<EvaluatedFeatureFlag>;
}

const defaultInitState: FeatureFlagsContext = {
  featureFlags: [],
  experiments: {},
};

export const FeatureFlagsContext =
  createContext<FeatureFlagsContext>(defaultInitState);

type FeatureFlagsProviderProps = {
  apiConfig: IApiConfig;
  initState?: FeatureFlagsContext;
  isLoggedIn?: boolean;
};

export const FeatureFlagsProvider: FC<
  FeatureFlagsProviderProps & PropsWithChildren
> = ({
  apiConfig,
  initState = defaultInitState,
  isLoggedIn = false,
  children,
}) => {
  const [experiments, setExperiments] = useState<ExperimentsType>(
    initState.experiments
  );
  const [featureFlags, setFeatureFlags] = useState<EvaluatedFeatureFlag[]>(
    initState.featureFlags
  );
  const [trackingProperties, setTrackingProperties] =
    useState<TrackingPropertiesType>(initState.trackingProperties ?? {});

  const shouldFetchFeatureFlags = useMemo(
    (): boolean => !initState?.experiments?.size && isLoggedIn,
    [initState?.experiments?.size, isLoggedIn]
  );

  const value: FeatureFlagsContext = useMemo(
    () => ({ featureFlags, experiments, trackingProperties }),
    [featureFlags, experiments, trackingProperties]
  );

  useEffect(() => {
    initFeatureFlagsState(
      shouldFetchFeatureFlags,
      apiConfig,
      defaultInitState
    ).then((newState: FeatureFlagsContext) => {
      setExperiments((currentState) => ({
        ...currentState,
        ...newState.experiments,
      }));
      setFeatureFlags((currentState) => [
        ...currentState,
        ...newState.featureFlags,
      ]);
      setTrackingProperties((currentState) => ({
        ...currentState,
        ...newState.trackingProperties,
      }));
    });
  }, [apiConfig, shouldFetchFeatureFlags, isLoggedIn]);

  return <FeatureFlagsContext.Provider value={value} children={children} />;
};
